import DocumentInfo from "@/components/DocumentInfo";
import PhotoInfo from "@/components/PhotoInfo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FileTextIcon, SquareUserRoundIcon } from "lucide-react";
import { useEffect, useState } from "react";

// TODO: import these from a shared source

type SessionStatus = "active" | "completed" | "canceled" | "expired" | "invalid";
type IdentifyingDocumentType = "birth_certificate" | "photo_id";

type SessionData = {
  id: string;
  status: SessionStatus;
  objective: string;
  allowedDocumentTypes: IdentifyingDocumentType;
  cancelUrl: string;
  rejections?: unknown | null;
  verificationSentTo: string;
  greeting: string;
};

type Policy = {
  id: string;
  title: string;
  policy: string;
};

type ConsentData = {
  signedName: string;
  affirmationText: string;
  policyId: string;
};

type VerificationFormData = {
  firstName: string;
  lastName: string;
  dob: string;
  email: string;
  photo: File | null;
  document: File | null;
  documentType: IdentifyingDocumentType | "";
  consent: ConsentData;
};

type VerificationResponse = {
  status: "SUCCESS" | "REJECTED" | "ERROR";
  successUrl?: string;
  rejections?: unknown;
};

// END - types

export const Route = createFileRoute("/verification")({
  component: VerificationPage,
});

function buildAffirmationText(guardian: boolean, policyTitle: string): string {
  return guardian
    ? `I certify that I am the parent or legal guardian of the applicant and accept the ${policyTitle} on their behalf.`
    : `I have read and accept the ${policyTitle}.`;
}

function VerificationPage() {
  const search = Route.useSearch();
  const sessionId = (search as any).sessionId as string | undefined;

  const [formData, setFormData] = useState<VerificationFormData>({
    firstName: "",
    lastName: "",
    dob: "",
    email: "",
    photo: null,
    document: null,
    documentType: "",
    consent: {
      signedName: "",
      affirmationText: "",
      policyId: "",
    },
  });

  const [uploadedPhotoKey, setUploadedPhotoKey] = useState<string | null>(null);
  const [uploadedDocumentKey, setUploadedDocumentKey] = useState<string | null>(null);

  // consent UI state (kept separate from formData, then synced into formData.consent below)
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [isGuardian, setIsGuardian] = useState(false);

  // fetch sesh data from query param
  const {
    data: sessionData,
    isLoading: sessionLoading,
    error: sessionError,
  } = useQuery<SessionData>({
    queryKey: ["session", sessionId],
    queryFn: async () => {
      if (!sessionId) {
        throw new Error("No sessionId provided");
      }
      const response = await fetch("/routes/session", {
        headers: {
          "Flash-Session": sessionId,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch session: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!sessionId,
  });

  // fetch policy for consent step
  const {
    data: policy,
    isLoading: policyLoading,
    isError: policyIsError,
    error: policyError,
    refetch: refetchPolicy,
  } = useQuery<Policy>({
    queryKey: ["policy", sessionId],
    queryFn: async () => {
      if (!sessionId) {
        throw new Error("No sessionId provided");
      }
      const response = await fetch("/routes/policy", {
        headers: {
          "Flash-Session": sessionId,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch policy: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!sessionId,
  });

  // keep formData.consent in sync with the consent UI state
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      consent: {
        signedName: signerName,
        affirmationText:
          policyAccepted && policy ? buildAffirmationText(isGuardian, policy.title) : "",
        policyId: policy?.id ?? "",
      },
    }));
  }, [signerName, isGuardian, policyAccepted, policy]);

  const isConsentComplete =
    policyAccepted && signerName.trim().length > 0 && !!policy && !!formData.consent.policyId;

  // media mutation
  const uploadMediaMutation = useMutation({
    mutationFn: async ({ file, type }: { file: File; type: "photo" | "document" }) => {
      if (!sessionId) {
        throw new Error("No session ID");
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type);

      const response = await fetch("/routes/v1/media", {
        method: "POST",
        headers: {
          "Flash-Session": sessionId,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      return response.json() as Promise<{ key: string }>;
    },
  });

  // Submit verification mutation
  const submitVerificationMutation = useMutation({
    mutationFn: async () => {
      if (!sessionId || !uploadedPhotoKey || !uploadedDocumentKey || !formData.documentType) {
        throw new Error("Missing required data");
      }

      const payload = {
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        dob: formData.dob || undefined,
        email: formData.email || undefined,
        photo: {
          key: uploadedPhotoKey,
        },
        document: {
          key: uploadedDocumentKey,
          type: formData.documentType,
        },
        consent: formData.consent,
      };

      const response = await fetch("/routes/v1/verification/identities", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Flash-Session": sessionId,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Verification failed: ${response.status}`);
      }

      return response.json() as Promise<VerificationResponse>;
    },
    onSuccess: (data) => {
      if (data.status === "SUCCESS" && data.successUrl) {
        window.location.href = data.successUrl;
      }
    },
  });

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "photo" | "document",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      [type]: file,
    }));

    try {
      const result = await uploadMediaMutation.mutateAsync({ file, type });
      if (type === "photo") {
        setUploadedPhotoKey(result.key);
      } else {
        setUploadedDocumentKey(result.key);
      }
    } catch (error) {
      console.error(`Failed to upload ${type}:`, error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitVerificationMutation.mutate();
  };

  if (!sessionId) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Missing Session ID</CardTitle>
            <CardDescription>
              Please provide a sessionId query parameter to access this page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (sessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Loading...</CardTitle>
            <CardDescription>Fetching session information</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Error</CardTitle>
            <CardDescription>{sessionError.message}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (sessionData?.status !== "active") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Session Not Active</CardTitle>
            <CardDescription>This verification session is {sessionData?.status}.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>{sessionData.greeting}</CardTitle>
          <CardDescription>Identity Verification</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                  placeholder="Enter your first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dob">Date of Birth</Label>
              <Input
                id="dob"
                type="date"
                value={formData.dob}
                onChange={(e) => setFormData((prev) => ({ ...prev, dob: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="photo" className="flex">
                <SquareUserRoundIcon width="24" height="24" />
                Photo
                <PhotoInfo />
              </Label>
              <Input
                id="photo"
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "photo")}
                disabled={uploadMediaMutation.isPending}
              />
              {uploadedPhotoKey && (
                <p className="text-sm text-green-600">Photo uploaded successfully</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="documentType">Document Type</Label>
              <Select
                value={formData.documentType}
                onValueChange={(documentType: string) =>
                  setFormData((prev) => ({
                    ...prev,
                    documentType: documentType as IdentifyingDocumentType,
                  }))
                }
              >
                <SelectTrigger id="documentType" className="w-full">
                  <SelectValue placeholder="Select a document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="birth_certificate">Birth Certificate</SelectItem>
                  <SelectItem value="photo_id">Photo ID</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="document" className="flex">
                <FileTextIcon width="24" height="24" />
                Document
                <DocumentInfo />
              </Label>
              <Input
                id="document"
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "document")}
                disabled={uploadMediaMutation.isPending}
              />
              {uploadedDocumentKey && (
                <p className="text-sm text-green-600">Document uploaded successfully</p>
              )}
            </div>

            <div className="space-y-3 rounded-lg border p-4">
              {policyLoading && <p className="text-sm text-muted-foreground">Loading policy…</p>}

              {policyIsError && (
                <div className="space-y-2">
                  <p className="text-sm text-red-600">
                    Couldn't load the policy
                    {policyError instanceof Error ? `: ${policyError.message}` : "."}
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={() => refetchPolicy()}>
                    Try again
                  </Button>
                </div>
              )}

              {policy && (
                <>
                  <div className="space-y-1">
                    <Label className="text-base">{policy.title}</Label>
                  </div>

                  <div className="h-32 overflow-y-auto rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap">
                    <p>{policy.policy}</p>
                    <br />
                    <p>
                      <span className="font-semibold">Full Biometric Policy:</span> Our publicly
                      available biometrics policy, which includes our retention and destruction
                      provisions, can be found{" "}
                      <a
                        className="font-medium text-primary underline"
                        href="/biometric-policy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        here
                      </a>
                      .
                    </p>
                  </div>

                  <label className="flex items-center gap-2 text-sm pt-4 pb-2">
                    <input
                      type="checkbox"
                      checked={isGuardian}
                      onChange={(e) => {
                        setIsGuardian(e.target.checked);
                        // the affirmation text changes with this toggle, so
                        // require the box to be re-checked against the new wording
                        setPolicyAccepted(false);
                      }}
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    <span>Signing as parent or guardian</span>
                  </label>

                  <label className="flex items-start gap-2 text-sm pt-2 pb-4">
                    <input
                      type="checkbox"
                      checked={policyAccepted}
                      onChange={(e) => setPolicyAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                    />
                    <span>{buildAffirmationText(isGuardian, policy.title)}</span>
                  </label>

                  <div className="space-y-2">
                    <Label htmlFor="signerName">Signature (type full name)</Label>
                    <Input
                      id="signerName"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      placeholder="Jane Doe"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label>Date</Label>
                    <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                      {new Date().toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {submitVerificationMutation.error && (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">
                Error: {(submitVerificationMutation.error as Error).message}
              </div>
            )}

            {submitVerificationMutation.data?.status === "REJECTED" && (
              <div className="rounded-md bg-yellow-50 p-4 text-sm text-yellow-800">
                Verification rejected. Please review and try again.
              </div>
            )}

            {submitVerificationMutation.data?.status === "ERROR" && (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">
                An error occurred during verification. Please try again.
              </div>
            )}

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={
                  !uploadedPhotoKey ||
                  !uploadedDocumentKey ||
                  !formData.documentType ||
                  !isConsentComplete ||
                  uploadMediaMutation.isPending ||
                  submitVerificationMutation.isPending
                }
                className="flex-1"
              >
                {submitVerificationMutation.isPending ? "Submitting..." : "Submit Verification"}
              </Button>
              {sessionData.cancelUrl && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => (window.location.href = sessionData.cancelUrl)}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
